/**
 * Recovering from a chunk that a deploy deleted out from under an open tab.
 *
 * THE FAILURE
 *   The app is code-split, so a route is a separate hashed file
 *   (AdminCaptures-CX0XX5Q6.js). A tab that loaded index.html before a deploy
 *   holds the OLD hashes; after the deploy those files are gone, so the first
 *   navigation to a lazy route throws "Failed to fetch dynamically imported
 *   module" and the boundary shows a crash card. Every one of these in
 *   production has been on an admin route, because those are the pages open
 *   across a deploy.
 *
 *   Nothing is actually broken: the user just needs the new index.html. So the
 *   fix is to reload, not to report a crash.
 *
 * WHY THE GUARD IS THE HARD PART
 *   A blind reload risks an infinite loop: if the chunk is missing for any
 *   reason a reload cannot fix (a genuinely broken deploy, an offline device, a
 *   caching proxy serving a stale index.html), the app would reload, fail, and
 *   reload forever — turning a one-page error into an unusable app.
 *
 *   So a reload is attempted at most once per RELOAD_WINDOW_MS. If we already
 *   tried and are still failing, we let the error through to the boundary,
 *   where at least it is visible and reportable.
 */

/** Marker for the last automatic reload, so a failed one is not retried. */
const STORAGE_KEY = "nx.staleChunkReloadAt";
/** A second failure inside this window means reloading is not the fix. */
export const RELOAD_WINDOW_MS = 30_000;

/**
 * Bundler-agnostic on purpose. The wording differs per browser — Chrome says
 * "Failed to fetch dynamically imported module", Safari "Importing a module
 * script failed", Firefox "error loading dynamically imported module" — and a
 * miss here just means the user sees the old crash card.
 *
 * Safari 27 has a second wording. The SPA fallback answers a missing asset
 * with index.html, so the import does not 404: it fetches fine and then fails
 * the type check with "'text/html' is not a valid JavaScript MIME type for
 * module script". Same stale chunk, same fix (error_log 912 and 914, the
 * admin analytics route across the 2026-09-29 and 2026-10-02 deploys).
 */
export function isStaleChunkError(err: unknown): boolean {
  if (!err) return false;
  const name = (err as { name?: string })?.name ?? "";
  if (name === "ChunkLoadError") return true;
  const message = String((err as { message?: string })?.message ?? err);
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /failed to load module script/i.test(message) ||
    /is not a valid javascript mime type for module script/i.test(message) ||
    /loading chunk \d+ failed/i.test(message)
  );
}

export interface ReloadDeps {
  now?: () => number;
  storage?: Pick<Storage, "getItem" | "setItem">;
  reload?: () => void;
}

/**
 * Reload once to pick up the current index.html. Returns true if a reload was
 * triggered, false if we already tried recently and the caller should let the
 * error surface instead.
 *
 * Storage access is wrapped: Safari private mode throws on write, and failing
 * to record the attempt must not stop the (single) reload from happening — it
 * only means the guard degrades, which is better than not recovering at all.
 */
function resolveStorage(deps: ReloadDeps): ReloadDeps["storage"] {
  try {
    return deps.storage ?? window.sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * Would reloadForStaleChunk() actually reload right now?
 *
 * Reads the marker and writes nothing, so unlike reloadForStaleChunk this is
 * safe to call during render — which is where RootErrorBoundary needs the
 * answer. getDerivedStateFromError runs in the render phase and must decide
 * whether to paint the crash card BEFORE componentDidCatch gets a chance to
 * reload, so it cannot use the side-effecting version to find out.
 */
export function wouldReloadForStaleChunk(deps: ReloadDeps = {}): boolean {
  const now = deps.now ?? (() => Date.now());
  const storage = resolveStorage(deps);
  try {
    const last = Number(storage?.getItem(STORAGE_KEY) ?? 0);
    if (last > 0 && now() - last < RELOAD_WINDOW_MS) return false;
  } catch {
    // Unreadable storage: we would reload. One extra reload is a much smaller
    // problem than never recovering from a deploy.
  }
  return true;
}

export function reloadForStaleChunk(deps: ReloadDeps = {}): boolean {
  const now = deps.now ?? (() => Date.now());
  const reload = deps.reload ?? (() => window.location.reload());
  const storage = resolveStorage(deps);

  // One timestamp for both the window check and the marker it writes.
  const at = now();
  if (!wouldReloadForStaleChunk({ ...deps, now: () => at, storage })) return false;

  try {
    storage?.setItem(STORAGE_KEY, String(at));
  } catch {
    /* guard degrades; still reload */
  }

  reload();
  return true;
}

/**
 * Handle a possible stale-chunk error. True means it was one AND a reload is
 * under way, so the caller should stop — no crash card, no error report,
 * because neither describes anything the user or we need to act on.
 */
export function handleStaleChunkError(err: unknown, deps: ReloadDeps = {}): boolean {
  if (!isStaleChunkError(err)) return false;
  return reloadForStaleChunk(deps);
}

/**
 * Pure counterpart to handleStaleChunkError: would that call reload?
 *
 * Same question, no side effects, for callers that must decide during render.
 * Keep the two in step — RootErrorBoundary asks this one in
 * getDerivedStateFromError and the real one in componentDidCatch, and they
 * have to agree about whether a card is coming.
 */
export function willHandleStaleChunkError(err: unknown, deps: ReloadDeps = {}): boolean {
  if (!isStaleChunkError(err)) return false;
  return wouldReloadForStaleChunk(deps);
}
